import React, { useEffect, useState } from "react";
import axios from "axios";
import { Flame, Clock, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Read-only shift history table. Admin/senior_manager can filter by
 * manager; managers see only their own history.
 */
export default function AdminShiftHistory() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    // Manager list — fetch once. Fail silently if the current user isn't
    // privileged (their own view still works).
    axios.get(`${API}/admin/staff`, { withCredentials: true })
      .then(r => setManagers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = filter ? { manager_id: filter } : {};
    axios.get(`${API}/admin/shift/history`, { params, withCredentials: true })
      .then(r => setShifts(r.data?.items || []))
      .catch(() => setShifts([]))
      .finally(() => setLoading(false));
  }, [filter]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", { day: "2-digit", month: "short",
                                          hour: "2-digit", minute: "2-digit" });
  };
  const durHours = (a, b) => {
    if (!a || !b) return "—";
    const h = (new Date(b) - new Date(a)) / 3600000;
    return h.toFixed(1) + "ч";
  };

  return (
    <div data-testid="admin-shift-history">
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Clock size={26} color="#C5A059" />
          <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "#111", margin: 0 }}>
            История смен
          </h2>
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b", marginTop: 6 }}>
          Все закрытые смены с автоподсчётом выручки, продаж и лидов.
        </div>
      </div>

      {managers.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <select data-testid="shift-history-filter" value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ background: "white", border: "1px solid #d1d5db", borderRadius: 6,
                      padding: "8px 12px", fontFamily: "Manrope", fontSize: 13 }}>
            <option value="">Все менеджеры</option>
            {managers.map(m => (
              <option key={m.id} value={m.id}>{m.name || m.email}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
          <Loader2 size={22} className="animate-spin" style={{ display: "inline" }} />
        </div>
      ) : shifts.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", fontFamily: "Manrope",
                       color: "#94a3b8", background: "white",
                       border: "1px dashed #e5e7eb", borderRadius: 8 }}>
          Закрытых смен ещё нет.
        </div>
      ) : (
        <div style={{ background: "white", border: "1px solid #e5e7eb",
                       borderRadius: 8, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse",
                            fontFamily: "Manrope", fontSize: 13 }}>
            <thead style={{ background: "#faf9f5" }}>
              <tr>
                <th style={th}>Менеджер</th>
                <th style={th}>Формат</th>
                <th style={th}>Начало</th>
                <th style={th}>Длительность</th>
                <th style={th}>Лиды</th>
                <th style={th}>Продажи</th>
                <th style={th}>Выручка</th>
                <th style={th}>План</th>
                <th style={{ ...th, textAlign: "center" }}>🔥</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map(s => {
                const st = s.auto_stats || {};
                const goal = s.goal || {};
                const pct = goal.revenue > 0
                  ? Math.round((st.revenue || 0) / goal.revenue * 100) : 0;
                return (
                  <tr key={s.id} style={{ borderTop: "1px solid #f1f1ee" }}>
                    <td style={td}>{s.manager_name || "—"}</td>
                    <td style={td}>
                      <span style={{
                        padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                        background: s.mode === "online" ? "#dbeafe" : "#fef3c7",
                        color: s.mode === "online" ? "#1e40af" : "#B45309",
                      }}>
                        {s.mode === "online" ? "ONLINE" : "OFFLINE"}
                      </span>
                    </td>
                    <td style={td}>{fmtDate(s.started_at)}</td>
                    <td style={td}>{durHours(s.started_at, s.ended_at)}</td>
                    <td style={td}>{st.leads_processed || 0}</td>
                    <td style={td}>{st.sales_count || 0}</td>
                    <td style={td}>{((st.revenue || 0) / 1000).toFixed(0)}к ₸</td>
                    <td style={{ ...td, color: pct >= 100 ? "#059669" : "#94a3b8", fontWeight: 700 }}>
                      {pct}%
                    </td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {s.plan_met && <Flame size={14} color="#B45309" style={{ display: "inline" }} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { textAlign: "left", padding: "10px 12px", color: "#374151",
              fontWeight: 600, fontSize: 10, textTransform: "uppercase",
              letterSpacing: 0.4, borderBottom: "1px solid #e5e7eb" };
const td = { padding: "10px 12px", color: "#0A0A0A", verticalAlign: "middle" };
